import {useQuery} from "@tanstack/react-query"
import moment from "moment"
import {getFullName, handleError} from "helpers"
import {useCalendarEventsViewSettings} from "hooks/useCalendarEventViewSettings"
import {get, isEmpty, keyBy} from "lodash"
import {useEffect, useState} from "react"
import {calendarService} from "services"
import {useModel} from "hooks"
import {CalendarEventType, CalendarStaticFilter, LinkedCalendarInfo, StaticOption} from "types/calendar"
import {checkIsTermMajorSystemEvent, getEndDateTime, getStartDateTime} from "helpers/calendar"
import {useGetExtraEventInfo} from "./useGetExtraEventInfo"

const DEFAULT_STATIC_OPTIONS: StaticOption[] = [
    {id: CalendarStaticFilter.Term, name: "Public Terms", isShow: true},
    {id: CalendarStaticFilter.ProgramStart, name: "Public Program Start", isShow: true},
    {id: CalendarStaticFilter.ProgramEnd, name: "Public Program End", isShow: true},
    {id: CalendarStaticFilter.WorkSchedules, name: "Public Work Schedules", isShow: true},
    {id: CalendarStaticFilter.Booked, name: "Booked Events", isShow: true},
    {id: CalendarStaticFilter.CourseSchedules, name: "Course Schedules", isShow: true},
    {id: CalendarStaticFilter.Invites, name: "Invites", isShow: true}
]

export const useGetCalendarEvents = () => {
    const model = useModel()
    const userId = +get(model, "user.id")
    const [dateRange, setDateRange] = useState(null)
    const [events, setEvents] = useState([])
    const {settings, isLoadingSettings} = useCalendarEventsViewSettings()
    const [linkedCalendars, setLinkedCalendars] = useState<LinkedCalendarInfo[]>([])
    const [staticOptions, setStaticOptions] = useState<StaticOption[]>([])
    const {getEventColor, getModuleTitle, getSubModuleTitle, getUsers} = useGetExtraEventInfo()

    const {data: myCalendars, isLoading: isLoadingCalendar} = useQuery(
        ["my-calendars", model.viewOtherUserCalendar?.id],
        () => calendarService.getMyCalendars({viewOtherUserId: model.viewOtherUserCalendar?.id}),
        {
            onError: (error) => handleError(error),
            initialData: [],
            onSuccess: (data) => {
                const linkedCalendars = data
                    .filter((item) => item.is_primary || !isEmpty(item.integration_data))
                    .map((item) => ({
                        isPrimary: item.is_primary,
                        id: !item.is_primary ? get(item, "integration_data.id") : null,
                        calendarId: item.calendar_id,
                        name: item.is_primary ? getFullName(model.viewOtherUserCalendar || model.user) : item.name,
                        provider: !item.is_primary ? get(item, "integration_data.account.provider") : null,
                        isShow: true
                    }))
                setLinkedCalendars(linkedCalendars)
            }
        }
    )

    const {refetch: reloadEvents, isFetching} = useQuery(
        ["calendar-events", staticOptions, dateRange, linkedCalendars],
        () => getEvents(),
        {
            enabled: !isEmpty(staticOptions) && !isEmpty(dateRange) && !isEmpty(linkedCalendars),
            onSuccess: (response) => {
                const data = convertEvents(response)
                setEvents(data)
            },
            onError: (error) => handleError(error)
        }
    )

    useEffect(() => {
        if (!isEmpty(settings)) {
            const newStaticOptions = DEFAULT_STATIC_OPTIONS.filter((item) => {
                switch (item.id) {
                    case CalendarStaticFilter.ProgramStart:
                        return settings.programStart
                    case CalendarStaticFilter.ProgramEnd:
                        return settings.programEnd
                    case CalendarStaticFilter.Term:
                        return settings.termStartAndEnd
                    default:
                        return true
                }
            })
            setStaticOptions(newStaticOptions)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settings])

    const getEvents = async () => {
        const {startStr: from, endStr: to} = dateRange
        const utcFrom = moment(from).utc().format()
        const utcTo = moment(to).utc().format()
        const checkedStaticOptionIds = staticOptions.filter((item) => item.isShow).map((item) => item.id)
        const checkedStaticFilter = Object.fromEntries(checkedStaticOptionIds.map((item) => [item, true]))
        const calendarIds = linkedCalendars.filter((item) => item.isShow).map((item) => item.calendarId)
        const filter = {
            from: utcFrom,
            to: utcTo,
            owner_user_ids: [userId],
            calendar_ids: calendarIds,
            ...checkedStaticFilter
        }
        const allEvents = await calendarService.getEvents({filter}, {viewOtherUserId: model.viewOtherUserCalendar?.id})
        let userIds = []
        if (allEvents.length) {
            const newUserIds = allEvents
                .filter((item) => item.owner_user_id && item.owner_user_id !== userId)
                .map((event) => event.owner_user_id)
            userIds = [...userIds, ...newUserIds]
        }
        const users = await getUsers(userIds)
        const usersKeyById = keyBy(users, "id")
        allEvents.forEach((event) => {
            const isCurrentUser = event.owner_user_id === userId
            event.ownerFullName = isCurrentUser
                ? getFullName(model.user)
                : getFullName(usersKeyById[event.owner_user_id])
        })

        return allEvents
    }

    const convertEvents = (initialEvents) => {
        return (initialEvents || []).map((event, index) => {
            const originalColor = event?.json_data?.color || "#1e90ff"
            const reminders = (event?.json_data?.notifications ?? []).map((value) => ({
                value,
                label: `${moment.duration(value / 60, "minutes").humanize()} before`
            }))
            const withSMS = !!event?.json_data?.with_sms
            const withPushNotification = !!event?.json_data?.with_push_notification
            const withEmail = !!event?.json_data?.with_email
            const eventSignInRequired = !!event?.json_data?.eventSignInRequired
            const eventTrackAttendanceMinutes = !!event?.json_data?.eventTrackAttendanceMinutes
            const attendedChangeToStatus = !!event?.json_data?.attendedChangeToStatus
            const notAttendedChangeToStatus = !!event?.json_data?.notAttendedChangeToStatus
            const completionWorkflow = !!event?.json_data?.completionWorkflow
            const {borderColor, color} = getEventColor(event)
            const allDay =
                checkIsTermMajorSystemEvent(event.type) || event?.json_data?.all_day || event?.json_data?.allDay
            const startDateTime = getStartDateTime(event)
            const endDateTime = getEndDateTime(event)

            return {
                id: event.object_id,
                start: startDateTime.format(),
                end: endDateTime.format(),
                originalColor,
                borderColor: borderColor,
                color: color,
                startDateTime,
                endDateTime,
                title: event.name,
                allDay,
                order: 1,
                classNames: "",
                initialEventIndex: index,
                description:
                    event.type === CalendarEventType.AppointmentSlot
                        ? get(event, "appointment_slot.booker_notes", event.description)
                        : event.description,
                isAppointmentSlot: event.type === CalendarEventType.AppointmentSlot,
                ownerUserId: event.owner_user_id,
                ownerFullName: event.ownerFullName,
                willGo: event.willGo,
                reminders,
                withSMS,
                withPushNotification,
                withEmail,
                eventSignInRequired,
                eventTrackAttendanceMinutes,
                attendedChangeToStatus,
                notAttendedChangeToStatus,
                completionWorkflow,
                isLesson: !isEmpty(event.courseScheduleInfo),
                type: event.type,
                isHoliday: event.type === CalendarEventType.WorkSchedule,
                schedule_suffix: event.courseScheduleInfo?.schedule_suffix,
                moduleTitle: getModuleTitle(event.json_data.eventType, event.courseInfo),
                subModuleTitle: getSubModuleTitle(event.json_data.sub_module_id, event.courseInfo),
                json_data: event.json_data,
                start_at_timezone: event.start_at_timezone,
                rrule: event.rrule
            }
        })
    }

    return {
        events,
        isLoading: isFetching || isLoadingCalendar || isLoadingSettings,
        linkedCalendars,
        staticOptions,
        primaryCalendarId: myCalendars.find((item) => item.is_primary)?.calendar_id,
        updateLinkedCalendars: setLinkedCalendars,
        updateStaticOptions: setStaticOptions,
        setEvents,
        reloadEvents,
        setDateRange
    }
}
